import React from "react";
import { Tooltip } from "@mui/material";
import Menu from "@mui/material/Menu";
import DatePicker from "react-datepicker";
import {
    addDays,
    addMonths,
    addYears,
    endOfMonth,
    endOfWeek,
    endOfYear,
    format,
    parse,
    startOfMonth,
    startOfWeek,
    startOfYear,
    subDays,
    subMonths,
    subYears,
} from "date-fns";

import { TextEntryFormControl } from "../TextEntryFormControl";
import { TextControlStyle } from "../../Utilities/FormDesignConstants";
import { StyledFormHelperText, StyledTextField } from "./CommonStyled";
import { IssueData } from "../../Utilities/ControlRenderContext";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { DateDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/DateDataDefinition";
import "react-datepicker/dist/react-datepicker.css";
import styled from "styled-components";
import { StyledHorizontalSplit } from "../../../CommonUI/BuildlingBlocks/StyledHorizontalSplit";
import { PropertyEditorSelect } from "../../../CommonUI/PropertyEditing/PropertyEditorSelect";
import { PropertyEditorBoolean } from "../../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

class XDateRangeEditUI extends React.Component<{ editMe: DateRangeFormControl }> {
    render() {
        return (
            <UISpacedBoxContent>
                {this.props.editMe.renderBaseDataControlEditor()}
                <PropertyEditorSelect
                    editObject={this.props.editMe}
                    propertyName="type"
                    label="Type"
                    options={["past", "future", "both"]}
                />
                <PropertyEditorBoolean
                    editObject={this.props.editMe}
                    propertyName="showRange"
                    label="Show Range"
                />
                {this.props.editMe.renderTextControlBasePropertyEditor()}
                {this.props.editMe.renderTextStyleSelectionEditor()}
            </UISpacedBoxContent>
        );
    }
}

export class DateRangeFormControl extends TextEntryFormControl {
    readonly __type: string = "Date Range Picker";
    type: "past" | "future" | "both" = "both";
    showRange: boolean = true;

    render() {
        return <XDateRangeUI me={this} />;
    }

    getValue = () => {
        if (this.valueName) {
            return this.getFormDataValue(this.valueName);
        }
    };

    setValue = (newValue: { startDate?: string; endDate?: string }) => {
        if (!this.valueName) return;
        const value = this.getValue();

        if (value) {
            this.setFormDataValue(this.valueName, { ...value, ...newValue });
        } else {
            this.setFormDataValue(this.valueName, newValue);
        }
    };

    renderEditUI(): JSX.Element | null {
        return <XDateRangeEditUI editMe={this} />;
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        if (!this.valueName) return;

        let def = new DateDataDefinition();
        def.name = this.valueName;
        return def;
    }

    getFormatedValue = () => {
        if (!this.valueName) return "";

        let dates: any[] = [];
        const { startDate, endDate } = this.getValue() || {};

        if (startDate) {
            dates.push(startDate);
        }

        if (endDate) {
            dates.push(endDate);
        }

        return dates.join(" - ");
    };

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(this, retData, "__type", "readonly", "type", "showRange");

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(this, data, "__type", "readonly", "type", "showRange");
    }
}

const staticPeriods = ["week", "month", "year", "7 days", "30 days"];

const XDateRangeUI = (props: { me: DateRangeFormControl }) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
    const [staticPeriod, setStaticPeriod] = React.useState<"last" | "this" | "next">("this");

    let style = props.me.getFormContext()?.form?.defaultTextStyle;

    if (props.me.overrideStyle && props.me.style) style = props.me.style;

    let size: "medium" | "small" = props.me.getFormContext()?.form?.defaultTextSize ?? "medium";

    if (props.me.overrideStyle && props.me.size) size = props.me.size;

    const issueData: IssueData | null = props.me.getFormContext()!.getControlContext(props.me)!.getRuntimeIssueData();
    const issueText: string | undefined | null = issueData?.text;
    const customWidth = props.me.fullWidth ? "100%" : props.me.width ? `${props.me.width}px` : "200px";

    const formatDate = (date: Date | null | (Date | null)[], idx: number) => {
        if (Array.isArray(date)) {
            if (date[idx]) {
                return format(date[idx]!, "MM/dd/yyyy");
            }
            return;
        }
        if (date) {
            return format(date, "MM/dd/yyyy");
        }

        return;
    };

    let { startDate: s, endDate: d } = props.me.getValue() || {};

    const startDate = s ? parse(s, "MM/dd/yyyy", new Date()) : undefined;
    const endDate = d ? parse(d, "MM/dd/yyyy", new Date()) : undefined;

    const onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const onSetStaticPeriod = (type: "week" | "month" | "year" | "7 days" | "30 days") => {
        // let startDate: string | undefined;
        // let endDate: string | undefined;
        if (staticPeriod === "this") {
            switch (type) {
                case "week":
                    props.me.setValue({
                        startDate: formatDate(startOfWeek(new Date()), 0),
                        endDate: formatDate(endOfWeek(new Date()), 0),
                    });
                    break;
                case "month":
                    props.me.setValue({
                        startDate: formatDate(startOfMonth(new Date()), 0),
                        endDate: formatDate(endOfMonth(new Date()), 0),
                    });
                    break;
                case "year":
                    props.me.setValue({
                        startDate: formatDate(startOfYear(new Date()), 0),
                        endDate: formatDate(endOfYear(new Date()), 0),
                    });
                    break;
            }
        }
        if (staticPeriod === "next") {
            let d: Date | undefined;
            switch (type) {
                case "week":
                    d = addDays(new Date(), 7);
                    props.me.setValue({
                        startDate: formatDate(startOfWeek(d), 0),
                        endDate: formatDate(endOfWeek(d), 0),
                    });
                    break;
                case "month":
                    d = addMonths(new Date(), 1);
                    props.me.setValue({
                        startDate: formatDate(startOfMonth(d), 0),
                        endDate: formatDate(endOfMonth(d), 0),
                    });
                    break;
                case "year":
                    d = addYears(new Date(), 1);
                    props.me.setValue({
                        startDate: formatDate(startOfYear(d), 0),
                        endDate: formatDate(endOfYear(d), 0),
                    });
                    break;
                case "7 days":
                    props.me.setValue({
                        startDate: formatDate(new Date(), 0),
                        endDate: formatDate(addDays(new Date(), 7), 0),
                    });
                    break;
                case "30 days":
                    props.me.setValue({
                        startDate: formatDate(new Date(), 0),
                        endDate: formatDate(addDays(new Date(), 30), 0),
                    });
                    break;
            }
        }
        if (staticPeriod === "last") {
            let d: Date | undefined;
            switch (type) {
                case "week":
                    d = subDays(new Date(), 7);
                    props.me.setValue({
                        startDate: formatDate(startOfWeek(d), 0),
                        endDate: formatDate(endOfWeek(d), 0),
                    });
                    break;
                case "month":
                    d = subMonths(new Date(), 1);
                    props.me.setValue({
                        startDate: formatDate(startOfMonth(d), 0),
                        endDate: formatDate(endOfMonth(d), 0),
                    });
                    break;
                case "year":
                    d = subYears(new Date(), 1);
                    props.me.setValue({
                        startDate: formatDate(startOfYear(d), 0),
                        endDate: formatDate(endOfYear(d), 0),
                    });
                    break;
                case "7 days":
                    props.me.setValue({
                        startDate: formatDate(subDays(new Date(), 7), 0),
                        endDate: formatDate(new Date(), 0),
                    });
                    break;
                case "30 days":
                    props.me.setValue({
                        startDate: formatDate(subDays(new Date(), 30), 0),
                        endDate: formatDate(new Date(), 0),
                    });
                    break;
            }
        }
    };

    let content: JSX.Element | null = null;

    switch (style) {
        case TextControlStyle.LABELED:
            content = (
                <form noValidate>
                    {props.me.label && <UILabel variant={"form_label"}>{props.me.label}</UILabel>}

                    <StyledTextField
                        size={size}
                        data-size={size}
                        variant={"outlined"}
                        fullWidth={props.me.fullWidth}
                        value={props.me.getFormatedValue()}
                        onChange={() => {}}
                        data-testid={`XDateRange-${props.me.valueName}-input`}
                        width={customWidth}
                        error={Boolean(issueText)}
                        InputProps={{
                            onFocus,
                            readOnly: true,
                            inputRef: props.me.ref,
                        }}
                    />
                    {issueText && issueText.length > 30 && (
                        <Tooltip
                            title={issueText}
                            arrow
                            placement="bottom"
                        >
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${props.me.valueName || "datepicker"}-hinttext`}
                            >
                                {issueText.slice(0, 30) + "..."}
                            </StyledFormHelperText>
                        </Tooltip>
                    )}
                    {((issueText && issueText.length < 31) || props.me.hintText) && (
                        <StyledFormHelperText
                            error={Boolean(issueText)}
                            data-testid={`${props.me.valueName || "datepicker"}-hinttext`}
                        >
                            {issueText ? issueText : props.me.hintText}
                        </StyledFormHelperText>
                    )}
                </form>
            );
            break;
        case TextControlStyle.SHADED:
            content = (
                <form noValidate>
                    <StyledTextField
                        size={size}
                        data-size={size}
                        variant={"filled"}
                        customstyle={"filled"}
                        fullWidth={props.me.fullWidth}
                        value={props.me.getFormatedValue()}
                        onChange={() => {}}
                        data-testid={`XDateRange-${props.me.valueName}-input`}
                        width={customWidth}
                        error={Boolean(issueText)}
                        label={props.me.label}
                        InputProps={{
                            onFocus,
                            readOnly: true,
                            inputRef: props.me.ref,
                        }}
                    />
                    {issueText && issueText.length > 30 && (
                        <Tooltip
                            title={issueText}
                            arrow
                            placement="bottom"
                        >
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${props.me.valueName || "datepicker"}-hinttext`}
                            >
                                {issueText.slice(0, 30) + "..."}
                            </StyledFormHelperText>
                        </Tooltip>
                    )}
                    {((issueText && issueText.length < 31) || props.me.hintText) && (
                        <StyledFormHelperText
                            error={Boolean(issueText)}
                            data-testid={`${props.me.valueName || "datepicker"}-hinttext`}
                        >
                            {issueText ? issueText : props.me.hintText}
                        </StyledFormHelperText>
                    )}
                </form>
            );
            break;
        case TextControlStyle.UNDERLINED:
            content = (
                <form noValidate>
                    <StyledTextField
                        size={size}
                        data-size={size}
                        variant={"standard"}
                        customstyle={"standard"}
                        fullWidth={props.me.fullWidth}
                        value={props.me.getFormatedValue()}
                        onChange={() => {}}
                        data-testid={`XDateRange-${props.me.valueName}-input`}
                        width={customWidth}
                        error={Boolean(issueText)}
                        label={props.me.label}
                        InputProps={{
                            onFocus,
                            readOnly: true,
                            inputRef: props.me.ref,
                        }}
                    />
                    {issueText && issueText.length > 30 && (
                        <Tooltip
                            title={issueText}
                            arrow
                            placement="bottom"
                        >
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${props.me.valueName || "datepicker"}-hinttext`}
                            >
                                {issueText.slice(0, 30) + "..."}
                            </StyledFormHelperText>
                        </Tooltip>
                    )}
                    {((issueText && issueText.length < 31) || props.me.hintText) && (
                        <StyledFormHelperText
                            error={Boolean(issueText)}
                            data-testid={`${props.me.valueName || "datepicker"}-hinttext`}
                        >
                            {issueText ? issueText : props.me.hintText}
                        </StyledFormHelperText>
                    )}
                </form>
            );
            break;
        case TextControlStyle.OUTLINE:
            content = (
                <form noValidate>
                    <StyledTextField
                        size={size}
                        data-size={size}
                        variant={"outlined"}
                        customstyle={"outlined"}
                        fullWidth={props.me.fullWidth}
                        value={props.me.getFormatedValue()}
                        onChange={() => {}}
                        data-testid={`XDateRange-${props.me.valueName}-input`}
                        width={customWidth}
                        error={Boolean(issueText)}
                        label={props.me.label}
                        InputProps={{
                            onFocus,
                            readOnly: true,
                            inputRef: props.me.ref,
                        }}
                    />
                    {issueText && issueText.length > 30 && (
                        <Tooltip
                            title={issueText}
                            arrow
                            placement="bottom"
                        >
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${props.me.valueName || "datepicker"}-hinttext`}
                            >
                                {issueText.slice(0, 30) + "..."}
                            </StyledFormHelperText>
                        </Tooltip>
                    )}
                    {((issueText && issueText.length < 31) || props.me.hintText) && (
                        <StyledFormHelperText
                            error={Boolean(issueText)}
                            data-testid={`${props.me.valueName || "datepicker"}-hinttext`}
                        >
                            {issueText ? issueText : props.me.hintText}
                        </StyledFormHelperText>
                    )}
                </form>
            );
            break;
    }

    return (
        <>
            {content}
            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={() => {
                    setAnchorEl(null);
                }}
                disableEnforceFocus
                disableRestoreFocus
                data-testid={`${props.me.valueName}-dateSelectorLayer`}
            >
                <StyledLayer showRange={props.me.showRange}>
                    <DatePicker
                        onChange={(date) => {
                            props.me.setValue({ startDate: formatDate(date, 0) });
                        }}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        open
                        shouldCloseOnSelect={false}
                        popperClassName="XDateRange-popper"
                        wrapperClassName="XDateRange-wrapper"
                        minDate={props.me.type === "future" ? new Date() : undefined}
                        maxDate={props.me.type === "past" ? new Date() : undefined}
                        data-testid={`XDateRange-${props.me.valueName}-start`}
                    />
                    <DatePicker
                        selected={endDate}
                        onChange={(date) => {
                            props.me.setValue({ endDate: formatDate(date, 1) });
                        }}
                        selectsEnd
                        open
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        shouldCloseOnSelect={false}
                        popperClassName="XDateRange-popper"
                        wrapperClassName="XDateRange-wrapper"
                        maxDate={props.me.type === "past" ? new Date() : undefined}
                        data-testid={`XDateRange-${props.me.valueName}-end`}
                    />
                    <StaticPicker>
                        <StaticPickerItem
                            onClick={() => {
                                props.me.setValue({
                                    startDate: formatDate(new Date(), 0),
                                    endDate: formatDate(new Date(), 1),
                                });
                            }}
                            data-testid={`XDateRange-${props.me.valueName}-today`}
                        >
                            Today
                        </StaticPickerItem>
                        <StaticPickerItem
                            onClick={() => {
                                props.me.setValue({
                                    startDate: formatDate(subDays(new Date(), 1), 0),
                                    endDate: formatDate(subDays(new Date(), 1), 1),
                                });
                            }}
                            data-testid={`XDateRange-${props.me.valueName}-yesterday`}
                        >
                            Yesterday
                        </StaticPickerItem>
                        <StyledHorizontalSplit
                            marginTop={16}
                            marginBottom={16}
                        />
                        <StaticPeriod>
                            <StaticPeriodButtons>
                                <TabButton
                                    selected={staticPeriod === "last"}
                                    onClick={() => {
                                        setStaticPeriod("last");
                                    }}
                                    data-testid={`XDateRange-${props.me.valueName}-lastPeriod`}
                                >
                                    Last
                                </TabButton>
                                <TabButton
                                    selected={staticPeriod === "this"}
                                    onClick={() => {
                                        setStaticPeriod("this");
                                    }}
                                    data-testid={`XDateRange-${props.me.valueName}-thisPeriod`}
                                >
                                    This
                                </TabButton>
                                <TabButton
                                    selected={staticPeriod === "next"}
                                    onClick={() => {
                                        setStaticPeriod("next");
                                    }}
                                    data-testid={`XDateRange-${props.me.valueName}-nextPeriod`}
                                >
                                    Next
                                </TabButton>
                            </StaticPeriodButtons>
                            {staticPeriods.map((s, idx) => {
                                if (staticPeriod === "this" && ["7 days", "30 days"].includes(s)) {
                                    return null;
                                }
                                return (
                                    <StaticPickerItem
                                        key={idx}
                                        onClick={() =>
                                            onSetStaticPeriod(s as "week" | "month" | "year" | "7 days" | "30 days")
                                        }
                                    >
                                        {s}
                                    </StaticPickerItem>
                                );
                            })}
                        </StaticPeriod>
                    </StaticPicker>
                </StyledLayer>
            </Menu>
        </>
    );
};

const StyledLayer = styled.div<{ showRange: boolean }>`
    display: flex;

    background: ${UIStyleConstants.WHITE_COLOR};
    box-sizing: border-box;

    > * {
        margin-left: 20px;
    }

    .XDateRange-popper {
        position: static !important;
        transform: none !important;

        .react-datepicker {
            border: none;
            border-radius: 0;

            font-style: normal;
            font-size: 14px;
            line-height: 140%;

            .react-datepicker__triangle {
                display: none;
            }

            .react-datepicker__header {
                background: ${UIStyleConstants.WHITE_COLOR};
                margin-bottom: 8px;

                border: none;

                .react-datepicker__day-name {
                    font-size: 12px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 35px;

                    letter-spacing: 0px;
                    text-align: center;

                    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

                    width: 35px;
                }
            }

            .react-datepicker__current-month {
                font-size: 14px;
                font-weight: 600;
                color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
                margin-bottom: 8px;
            }

            .react-datepicker__day {
                font-size: 14px;
                font-style: normal;
                font-weight: 400;
                line-height: 35px;
                letter-spacing: 0em;
                text-align: center;
                color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

                width: 35px;

                border-radius: 50%;

                &.react-datepicker__day--range-start,
                &.react-datepicker__day--range-end {
                    color: ${UIStyleConstants.WHITE_COLOR} !important;
                    background: ${UIStyleConstants.BLUE_COLOR} !important;
                }

                &.react-datepicker__day--in-range {
                    ${(props) =>
                        props.showRange
                            ? `
                                background: ${UIStyleConstants.GRAY_LIGHT_COLOR};
                                color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
                            `
                            : `
                                color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
                                background: ${UIStyleConstants.WHITE_COLOR};
                            `}
                }
            }
        }
    }

    .XDateRange-wrapper {
        display: none;
    }
`;

const StaticPicker = styled.div`
    border-left: solid 1px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    padding: 32px;
    display: flex;
    justify-content: center;
    flex-direction: column;
`;

const StaticPickerItem = styled.div`
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 17px;
    letter-spacing: 0em;
    text-align: left;

    cursor: pointer;

    padding: 4px 0;

    &:hover {
        color: ${UIStyleConstants.BLUE_COLOR};
    }
`;

const StaticPeriod = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const TabButton = styled.div<{ selected: boolean }>`
    cursor: pointer;
    //styleName: text/Inter Medium 12px;

    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: center;
    padding-bottom: 2px;

    ${(props) =>
        props.selected
            ? `
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        border-bottom: solid 1px  ${UIStyleConstants.BLUE_COLOR};
    `
            : `
        color:  ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        border-bottom: solid 1px translate;
    `}
`;

const StaticPeriodButtons = styled.div`
    display: flex;

    margin-bottom: 12px;

    > div {
        margin-left: 12px;

        &:first-child {
            margin-left: 0;
        }
    }
`;
